import { Box, Container, Typography } from '@mui/material';
import { Accordion, Button } from '@ghs/components';
import { useUnit } from 'effector-react';
import useMountedEvent from '../../hooks/useMountedEvent';
import { hasKnowledgeBasePermission, hasReleaseNotesPermission } from '../../services/PermissionService/PermissionService';
import { $$gcp } from '../../services/GcpService/model';
import { $$documentation } from './model';
import { $$app } from '../../components/App/model';
import ReleaseNotesDialog from './ReleaseNotesDialog';
import SnackbarRoot from '../../components/SnackbarRoot/SnackbarRoot.jsx';
import { useTheme } from '@mui/material/styles';

export default function DocumentationPage() {
  useMountedEvent($$documentation.pageMounted);

  const userDetails = useUnit($$app.$userDetails);
  const faqs = useUnit($$gcp.$faqs);

  const docHtml = useUnit($$documentation.$documentationHtml);
  const theme = useTheme();

  const handleClick = e => {
    // Open the image in a new tab
    if (e.target.tagName === 'IMG') {
      window.open(e.target.src, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <ReleaseNotesDialog />
      <SnackbarRoot snackbarProps={{ autoHideDuration: 3000 }} />

      <Container id="documentation-container">
        {hasReleaseNotesPermission(userDetails?.permissions) && (
          <Button color="primary" onClick={() => $$documentation.releaseNotesButtonClicked()} size="medium" variant="contained" sx={{ mb: 0 }}>
            Release Notes
          </Button>
        )}

        {hasKnowledgeBasePermission(userDetails?.permissions) ? (
          <Box
            id="knowledge-base-container"
            sx={{
              ...theme.typography.body1,
              color: theme.palette.text.primary,
              '& img': {
                width: 'auto',
                height: 'auto',
                maxWidth: '400px',
                // Link pointer
                '&:hover': {
                  cursor: 'pointer'
                }
              },
              // Override default style to visited links and keep them all blue
              '& a, & a:visited': {
                color: 'blue'
              },
              // Fox error with fixed headers overlapping anchor targets.
              // The application bar is defined with height 50px so we'll use 70px to take margin into account
              '& h1[id], & h2[id], & h3[id], & h4[id], & h5[id], & h6[id], & div#document-top': {
                scrollMarginTop: '70px'
              },
              // Indent elements in TOC
              '& .index_level_1': {
                marginLeft: '18pt'
              },
              // Custom styles for MT and ST documentation
              ...(userDetails.isSt
                ? {
                    '& .c7': { fontWeight: 400 },
                    '& .c3': { fontWeight: 700 },
                    '& .c0': { fontWeight: 400 },
                    '& .c12': { marginLeft: 'calc(1em + 5pt)' },
                    '& .c27': { marginLeft: 'calc(1em + 15pt)' },
                    '& .c38': { marginLeft: 'calc(1em + 15pt)' }
                  }
                : {
                    '& .c4': { fontWeight: 700 },
                    '& .c0': { fontWeight: 400 },
                    '& .c1': { fontWeight: 700 },
                    '& .c8': { marginLeft: 'calc(1em + 15pt)' },
                    '& .c19': { marginLeft: 'calc(1em + 15pt)' },
                    '& .c36': { marginLeft: 'calc(1em + 15pt)' },
                    '& .c37': { marginLeft: 'calc(1em + 15pt)' },
                    '& .c40': { marginLeft: 'calc(1em + 15pt)' },
                    '& span.c12': { marginLeft: 'calc(1em + 5pt)' }
                  })
            }}
            onClick={handleClick}
            dangerouslySetInnerHTML={{ __html: docHtml }}
          />
        ) : (
          <Container id="faq-container">
            <Typography variant="h4" color="text.primary" sx={{ mt: 2, mb: 6 }}>
              Frequently Asked Questions
            </Typography>

            {faqs.map((faq, index) => {
              return (
                <Accordion key={`faq-${index}`} heading={faq.question}>
                  <Container>
                    <Typography variant="string">
                      <span>{faq.answer}</span>
                    </Typography>
                  </Container>
                </Accordion>
              );
            })}
          </Container>
        )}
      </Container>
    </>
  );
}
